/**
 * « Sur site » for a manager (spec C §4): who is inside right now, read-only.
 * Recording an exit stays a desk action (/poste/aujourdhui). Same rows as the
 * desk (`onSiteQuery`), so the two numbers cannot disagree.
 */
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { Card } from "@gately/ui/card";
import { StatusBadge } from "@gately/ui/status-badge";
import { unitLabel } from "@gately/validators";

import { orpc } from "~/lib/orpc";
import { useOrgTimeZone } from "~/lib/use-me";
import { resolvePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/sur-site")({
  // Same gate as /activite. The server enforces it too (assertCanView +
  // assertExitTrackingOn); hiding the page is a courtesy, never the boundary.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    const exitOn = (me.activeOrg?.exitTracking ?? "off") !== "off";
    if (!resolvePermissions(me).canViewActivity || !exitOn) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: SurSitePage,
});

function SurSitePage() {
  const timeZone = useOrgTimeZone();
  const terms = useTerms();
  const { data, isError } = useQuery({
    ...orpc.dashboard.onSiteList.queryOptions(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const time = (d: Date | string) =>
    new Intl.DateTimeFormat("fr-FR", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(d));
  const now = data ? new Date(data.asOf) : new Date();

  return (
    <div className="mx-auto w-full max-w-4xl px-6 py-8">
      <h1 className="text-2xl font-bold tracking-tight">Sur site</h1>
      <p className="text-muted-foreground mt-1 text-sm">
        {data
          ? `${data.headcount} personne${data.headcount > 1 ? "s" : ""} · à ${time(data.asOf)}`
          : isError
            ? "Impossible de charger la liste."
            : "Chargement…"}
      </p>

      {data && data.groups.length === 0 ? (
        <Card className="mt-6 p-8 text-center text-[13px] text-neutral-400">
          Personne n'est sur site.
        </Card>
      ) : null}

      <div className="mt-6 flex flex-col gap-3">
        {data?.groups.map((g) => (
          <Card key={g.rows[0]?.id} className="p-4">
            {g.inviteId ? (
              <div className="mb-2 text-[12px] font-medium text-neutral-500">
                Groupe · {g.headcount} personnes
              </div>
            ) : null}
            {g.rows.map((r) => (
              <div key={r.id} className="flex items-center gap-3 py-1.5">
                <span className="w-12 shrink-0 font-mono text-[12px] text-neutral-500">
                  {time(r.createdAt)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-neutral-800">
                    {r.guestName}
                    {r.actualHeadcount && r.actualHeadcount > 1
                      ? ` (+${r.actualHeadcount - 1})`
                      : ""}
                  </div>
                  <div className="truncate text-[12px] text-neutral-400">
                    {r.unitNumber
                      ? unitLabel(
                          {
                            name: r.unitName,
                            building: r.unitBuilding,
                            number: r.unitNumber,
                          },
                          terms.orgLabel,
                        )
                      : terms.orgLabel}
                    {r.gateName ? ` · ${r.gateName}` : ""}
                    {r.badgeCode ? ` · badge ${r.badgeCode}` : ""}
                  </div>
                </div>
                {r.visitorType === "intervenant" ? (
                  <StatusBadge tone="info">Intervenant</StatusBadge>
                ) : null}
                {r.validTo && new Date(r.validTo).getTime() < now.getTime() ? (
                  <StatusBadge tone="warning">Pass terminé</StatusBadge>
                ) : null}
              </div>
            ))}
          </Card>
        ))}
      </div>
    </div>
  );
}
